import { Grid2 as Grid, Stack, Tooltip, Typography } from '@mui/material';
import DropdownIcon from 'components/common/dropdown-icon/DropdownIcon';
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IDomain, setSelectedDomains, ICustomService } from 'store/conciergeSlice';
import useBundleTranslation from 'i18n';
import DomainMenuItem from './parts/DomainMenuItem';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';
import IconHandler from 'components/common/icon/IconHandler';

export interface IMenuDomain {
    name: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    title: string;
    description?: string;
    suggestedPrompts?: string[];
    selected: boolean;
    menuSelected: boolean;
    header: boolean;
    isGroupHeader?: boolean;
    isDivider?: boolean;
    hasAuth?: boolean;
    needsAuth?: boolean;
    authInfo?: ICustomService['authInfo'];
    serviceName?: string;
    reportDataState?: 'no_access' | 'enabled';
}

const BUILT_IN_DOMAIN_NAMES = ['search', 'glossary', 'faq', 'docs'];
const OTHER_DOMAIN_NAMES = ['glossary', 'faq', 'docs'];

interface IProps {
    loading: boolean;
    onSelectDomainPrompt: (prompt: string) => void;
    menuOpened: boolean;
    setMenuOpened: (opened: boolean) => void;
}

export default function DomainSelector({ loading, onSelectDomainPrompt, menuOpened, setMenuOpened }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const [menuSelectedDomains, setMenuSelectedDomains] = useState<string[]>(selectedDomains);
    const [collapsedGroups, setCollapsedGroups] = useState<string[]>([]);
    const dropdownHandleCloseRef = useRef<null | ((event: any) => void)>(null);
    const skipNextMenuStateChangeRef = useRef<boolean>(false);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.footerDomainSelector) as typeof defaultStyles;
    }, [customStyles]);

    const createTokenInputIfNeeded = (tokenFieldName: string) => {
        const existingInput = document.querySelector(`[name="${tokenFieldName}"]`);
        if (!existingInput) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = tokenFieldName;
            input.setAttribute('data-test', tokenFieldName);
            input.value = '';
            document.body.appendChild(input);
        }
    };

    useEffect(() => {
        availableDomains.forEach((domain) => {
            domain.customServices?.forEach((service) => {
                if (service.authInfo?.tokenFieldName) {
                    createTokenInputIfNeeded(service.authInfo.tokenFieldName);
                }
            });
        });
    }, [availableDomains]);

    const domainTokenFields = useMemo(() => {
        const fields: { [domainName: string]: string } = {};
        availableDomains.forEach((domain) => {
            const serviceWithAuth = domain.customServices?.find((service) => service.authInfo?.tokenFieldName);
            if (serviceWithAuth?.authInfo?.tokenFieldName) {
                fields[domain.name] = serviceWithAuth.authInfo.tokenFieldName;
            }
        });
        return fields;
    }, [availableDomains]);

    useEffect(() => {
        if (menuOpened) {
            setMenuSelectedDomains(selectedDomains);
            return;
        }

        if (skipNextMenuStateChangeRef.current) {
            skipNextMenuStateChangeRef.current = false;
            return;
        }

        dispatch(setSelectedDomains(menuSelectedDomains));
        setMenuSelectedDomains([]);
    }, [menuOpened]);

    const menuSelectDomain = (name: string) => {
        const result = name === '' || menuSelectedDomains.includes(name) ? [] : [name];

        setMenuSelectedDomains(result);
        dispatch(setSelectedDomains(result));

        setTimeout(() => {
            skipNextMenuStateChangeRef.current = true;
            dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
        }, 0);
    };

    const makeDomainItem = (domain: IDomain): IMenuDomain => {
        const serviceWithAuth = domain.customServices?.find((service) => service.authInfo);
        return {
            name: domain.name,
            icon_ind: domain.icon_ind,
            icon_name: domain.icon_name,
            icon_data: domain.icon_data,
            title: domain.title,
            description: domain.description,
            suggestedPrompts: domain.suggestedPrompts,
            selected: selectedDomains.includes(domain.name),
            menuSelected: menuSelectedDomains.includes(domain.name),
            header: false,
            hasAuth: !!serviceWithAuth,
            authInfo: serviceWithAuth?.authInfo,
            serviceName: serviceWithAuth?.name,
            reportDataState: domain.reportDataState,
        };
    };

    const makeDivider = (): IMenuDomain => ({
        name: '',
        icon_ind: '',
        icon_name: '',
        icon_data: '',
        title: '',
        selected: false,
        menuSelected: false,
        header: false,
        isDivider: true,
    });

    const makeGroupHeader = (title: string): IMenuDomain => ({
        name: '',
        icon_ind: '',
        icon_name: '',
        icon_data: '',
        title,
        selected: false,
        menuSelected: false,
        header: false,
        isGroupHeader: true,
    });

    const preparedDomains: IMenuDomain[] = useMemo(() => {
        if (availableDomains.length === 0) return [];

        const items: IMenuDomain[] = [];

        // All Sources — top standalone item
        items.push({
            name: '',
            icon_ind: conciergeSettings.bot_icon_ind,
            icon_name: conciergeSettings.bot_icon_name,
            icon_data: conciergeSettings.bot_icon_data,
            title: t('footer.all_sources'),
            selected: selectedDomains.length === 0,
            menuSelected: menuSelectedDomains.length === 0,
            header: false,
        });

        // Search — standalone item (built-in content search domain)
        const searchDomain = availableDomains.find((d) => d.name === 'search');
        if (searchDomain) {
            items.push(makeDomainItem(searchDomain));
        }

        // Data group — report_data first (if present), then custom domains
        const reportDataDomain = availableDomains.find((d) => d.name === 'report_data');
        const customDomains = availableDomains.filter(
            (d) => !BUILT_IN_DOMAIN_NAMES.includes(d.name) && d.name !== 'report_data',
        );
        const dataDomains = reportDataDomain ? [reportDataDomain, ...customDomains] : customDomains;
        if (dataDomains.length > 0) {
            items.push(makeGroupHeader(t('footer.group_data')));
            dataDomains.forEach((d, idx) => {
                items.push(makeDomainItem(d));
                if (d.name === 'report_data' && customDomains.length > 0) {
                    items.push(makeDivider());
                }
            });
        }

        // Other group — built-in types except search
        const otherDomains = availableDomains.filter((d) => OTHER_DOMAIN_NAMES.includes(d.name));
        if (otherDomains.length > 0) {
            items.push(makeGroupHeader(t('footer.group_other')));
            otherDomains.forEach((d) => items.push(makeDomainItem(d)));
        }

        return items;
    }, [availableDomains, selectedDomains, menuSelectedDomains, conciergeSettings, t]);

    const selectedDomainBadges = useMemo(() => {
        return selectedDomains
            .map((name) => availableDomains.find((d) => d.name === name))
            .filter(Boolean) as IDomain[];
    }, [availableDomains, selectedDomains]);

    const makeTriggerLabel = (iconInd: string, iconName: string, iconData: string, title: string) => {
        const iconProps = {
            type: iconInd,
            value: iconInd === 'upload' ? `data:image/png;base64,${iconData}` : iconName,
        };
        return (
            <Stack direction="row" alignItems="center" gap="4px" sx={{ overflow: 'hidden', maxWidth: '180px' }}>
                <IconHandler icon={iconProps} sx={{ fontSize: '14px', flexShrink: 0 }} />
                <Typography noWrap sx={{ fontSize: '13px' }}>
                    {title}
                </Typography>
            </Stack>
        );
    };

    const allSourcesLabel = useMemo(
        () =>
            makeTriggerLabel(
                conciergeSettings.bot_icon_ind,
                conciergeSettings.bot_icon_name,
                conciergeSettings.bot_icon_data,
                t('footer.all_sources'),
            ),
        [conciergeSettings, t],
    );

    const selectedDomainLabel = useMemo(() => {
        if (selectedDomainBadges.length === 0) return undefined;
        const domain = selectedDomainBadges[0];
        return makeTriggerLabel(domain.icon_ind, domain.icon_name, domain.icon_data, domain.title);
    }, [selectedDomainBadges]);

    const onSelectPrompt = (domain: string, prompt: string) => {
        onSelectDomainPrompt(prompt);
        dispatch(setSelectedDomains([domain]));
        skipNextMenuStateChangeRef.current = true;
        dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
    };

    const toggleGroup = (title: string) => {
        setCollapsedGroups((prev) => (prev.includes(title) ? prev.filter((g) => g !== title) : [...prev, title]));
    };

    const visibleDomains = useMemo(() => {
        let currentGroupCollapsed = false;
        return preparedDomains.filter((domain) => {
            if (domain.isGroupHeader) {
                currentGroupCollapsed = collapsedGroups.includes(domain.title);
                return true;
            }
            return !currentGroupCollapsed;
        });
    }, [preparedDomains, collapsedGroups]);

    const closeMenu = () => {
        skipNextMenuStateChangeRef.current = true;
        dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
    };

    // Drive the trigger from resolved badges, not the raw selection: an
    // unresolvable selection (e.g. name/case mismatch) must fall back to
    // "All Sources" instead of rendering an empty trigger.
    const hasSelectedDomain = selectedDomainBadges.length > 0;
    const hasNoSources = availableDomains.length === 0;
    const noSourcesMsg = t('footer.no_sources_available', 'No sources available');

    return (
        <Grid container direction="row" alignItems="center" justifyContent={'flex-start'} spacing={0.5}>
            <Tooltip title={hasNoSources ? noSourcesMsg : ''}>
                <span style={{ display: 'inline-flex' }}>
                    <DropdownIcon
                        onChangeState={setMenuOpened}
                        icon={'chevron-down'}
                        iconFirst={false}
                        iconSize={'16px'}
                        placement={'top-end'}
                        handleCloseRef={dropdownHandleCloseRef}
                        onClickInside={(event, handleClose) => {
                            if ((event as any)?.__miCloseDropdown) {
                                skipNextMenuStateChangeRef.current = true;
                                handleClose(event as any);
                            }
                        }}
                        iconButtonProps={styles.iconButton}
                        disabled={loading || hasNoSources}
                        label={hasSelectedDomain ? selectedDomainLabel : allSourcesLabel}
                        popperSx={styles.dropdownPanel}
                    >
                        <Stack sx={styles.menuContainer} spacing={0}>
                            {visibleDomains.map((domain, idx) => (
                                <DomainMenuItem
                                    key={idx}
                                    domain={domain}
                                    selectDomain={menuSelectDomain}
                                    onSelectPrompt={onSelectPrompt}
                                    closeMenu={closeMenu}
                                    onToggleGroup={toggleGroup}
                                    isGroupCollapsed={
                                        domain.isGroupHeader ? collapsedGroups.includes(domain.title) : undefined
                                    }
                                />
                            ))}
                        </Stack>
                    </DropdownIcon>
                </span>
            </Tooltip>
        </Grid>
    );
}
